/*
 * AIMER — Infinite scroll para páginas de coleção e busca (tema Dawn).
 * Sem dependências. Usa IntersectionObserver com fallback de scroll/resize.
 * A paginação numérica permanece no DOM (oculta quando ativo, restaurada em
 * caso de erro). Reinicializa automaticamente após filtros/ordenação.
 *
 * Obs.: na coleção o #product-grid é o próprio <ul>; na busca é uma <div>
 * que envolve o <ul>. gridList() resolve os dois casos.
 *
 * ---------------------------------------------------------------------------
 * CORREÇÃO 2026-07-21 — "os últimos produtos ficam repetindo sem parar":
 *
 *   A próxima página era guardada só na variável local `url`; o atributo
 *   `data-next-url` do `.pagination-wrapper` no DOM nunca era atualizado e
 *   seguia apontando para a PÁGINA 2 (valor do render inicial do Liquid).
 *
 *   Ao chegar na última página, teardown() REMOVIA o sentinel do DOM. Como o
 *   MutationObserver observa o pai do #ProductGridContainer com subtree:true,
 *   os próprios <li> recém-anexados disparavam o observer → setup() rodava de
 *   novo. A única guarda contra re-inicialização era a presença do sentinel;
 *   sem ele, o script relia data-next-url (= página 2) e recomeçava a anexar
 *   tudo desde o início, em loop infinito.
 *
 *   Duas travas independentes agora:
 *     1. teardown() NÃO remove mais o sentinel (ele permanece como guarda) e
 *        zera o data-next-url, para não haver como re-armar.
 *     2. Deduplicação por handle do produto: uma peça já exibida nunca é
 *        anexada de novo.
 *
 *   Nenhuma das duas atrapalha filtro/ordenação: o Dawn substitui o innerHTML
 *   de #ProductGridContainer, então sentinel e wrapper são recriados pelo
 *   Liquid com o data-next-url correto e o setup() re-arma normalmente.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (!('fetch' in window)) return;

  var SENTINEL_CLASS = 'aimer-infinite-sentinel';
  var STYLE_ID = 'aimer-infinite-style';
  var THRESHOLD = 600; // px antes do sentinel entrar na viewport

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      '.aimer-infinite-sentinel{display:block !important;width:100%;height:1px;margin:0;padding:0;}' +
      '.aimer-infinite-spinner{display:none;justify-content:center;align-items:center;padding:2.4rem 0;width:100%;}' +
      '.aimer-infinite-spinner.is-active{display:flex;}' +
      '.aimer-infinite-spinner__dot{width:2.2rem;height:2.2rem;border-radius:50%;border:2px solid rgba(var(--color-foreground,18 18 18),.18);border-top-color:rgba(var(--color-foreground,18 18 18),.7);animation:aimer-inf-spin .7s linear infinite;}' +
      '@keyframes aimer-inf-spin{to{transform:rotate(360deg);}}';
    document.head.appendChild(s);
  }

  // Retorna o <ul> que contém os cards, a partir do elemento #product-grid.
  function gridList(gridEl) {
    if (!gridEl) return null;
    if (gridEl.tagName === 'UL') return gridEl;
    return gridEl.querySelector('ul.product-grid') || gridEl.querySelector('ul');
  }

  function stripAnimations(el) {
    var nodes = [el].concat(Array.prototype.slice.call(el.querySelectorAll('.scroll-trigger')));
    nodes.forEach(function (n) {
      n.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in');
      n.removeAttribute('data-cascade');
      if (n.style) {
        n.style.removeProperty('--animation-order');
        n.style.opacity = '';
        n.style.transform = '';
      }
    });
  }

  // Chave única da peça (caminho /products/handle), usada na deduplicação.
  function itemKey(li) {
    var a = li.querySelector ? li.querySelector('a[href*="/products/"]') : null;
    if (!a) return null;
    var href = a.getAttribute('href') || '';
    return href.split('?')[0].split('#')[0];
  }

  function findContainer() {
    return document.getElementById('ProductGridContainer');
  }

  function setup(container) {
    if (!container) return;
    var gridEl = container.querySelector('#product-grid');
    var list = gridList(gridEl);
    var wrapper = container.querySelector('.pagination-wrapper');
    if (!list || !wrapper) return;
    if (container.querySelector('.' + SENTINEL_CLASS)) return; // já inicializado
    var nextUrl = wrapper.getAttribute('data-next-url');
    if (!nextUrl) return; // sem próxima página

    injectStyle();
    wrapper.setAttribute('hidden', 'hidden'); // esconde a paginação numérica

    var sentinel = document.createElement('div');
    sentinel.className = SENTINEL_CLASS;
    sentinel.setAttribute('aria-hidden', 'true');

    var spinner = document.createElement('div');
    spinner.className = 'aimer-infinite-spinner';
    spinner.setAttribute('role', 'status');
    spinner.innerHTML = '<span class="aimer-infinite-spinner__dot"></span>';

    wrapper.parentNode.insertBefore(spinner, wrapper);
    wrapper.parentNode.insertBefore(sentinel, spinner);

    var loading = false;
    var done = false;
    var url = nextUrl;
    var observer = null;
    var rafPending = false;

    // Peças já exibidas, para nunca anexar a mesma peça duas vezes.
    var seen = Object.create(null);
    Array.prototype.forEach.call(list.querySelectorAll(':scope > li'), function (li) {
      var k = itemKey(li);
      if (k) seen[k] = true;
    });

    function teardown() {
      done = true;
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      // O sentinel PERMANECE no DOM de propósito: é ele que impede o setup()
      // de re-armar quando o MutationObserver dispara pelos <li> anexados.
      // Zeramos o data-next-url (que ainda apontava para a página 2).
      wrapper.removeAttribute('data-next-url');
      if (spinner.parentNode) spinner.parentNode.removeChild(spinner);
    }

    function sentinelNear() {
      var rect = sentinel.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.top <= vh + THRESHOLD;
    }

    function maybeLoad() {
      if (!loading && !done && sentinelNear()) load();
    }

    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      var cb = function () { rafPending = false; maybeLoad(); };
      if (window.requestAnimationFrame) window.requestAnimationFrame(cb);
      else setTimeout(cb, 100);
    }

    function load() {
      if (loading || done || !url) return;
      loading = true;
      spinner.classList.add('is-active');
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newList = gridList(doc.querySelector('#product-grid'));
          var items = newList ? newList.querySelectorAll(':scope > li') : [];
          var added = 0;
          Array.prototype.forEach.call(items, function (li) {
            // Deduplicação: ignora peça que já está na grade.
            var k = itemKey(li);
            if (k) {
              if (seen[k]) return;
              seen[k] = true;
            }
            var node = document.importNode(li, true);
            stripAnimations(node);
            list.appendChild(node);
            added++;
          });
          var newWrapper = doc.querySelector('.pagination-wrapper');
          url = newWrapper ? newWrapper.getAttribute('data-next-url') : null;
          loading = false;
          spinner.classList.remove('is-active');
          // Sem próxima página, leva vazia, ou só com peças já exibidas:
          // chegamos ao fim de verdade.
          if (!url || items.length === 0 || added === 0) {
            teardown();
          } else {
            setTimeout(maybeLoad, 60); // continua se ainda estiver visível
          }
        })
        .catch(function () {
          loading = false;
          spinner.classList.remove('is-active');
          wrapper.removeAttribute('hidden'); // restaura paginação numérica
          teardown();
        });
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) maybeLoad();
      }, { rootMargin: THRESHOLD + 'px 0px' });
      observer.observe(sentinel);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    setTimeout(maybeLoad, 100); // caso a página já esteja curta o suficiente
  }

  function init() { setup(findContainer()); }

  // Reinicializa após filtros/ordenação (o Dawn substitui o HTML do grid).
  var scheduled = false;
  var mo = new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { scheduled = false; setup(findContainer()); }, 50);
  });

  function boot() {
    init();
    var c = findContainer();
    var target = (c && c.parentNode) ? c.parentNode : document.body;
    if (target) mo.observe(target, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
